import React, { useEffect, useState } from 'react'
import { LoginSysAdmin } from '../../../role/components/login-sys-admin'
import { lmsAdmin } from '../api'
import '../lms-admin.scss'
import { LmsIcon } from '../lms-icon'

// Branding & Display (launcher dynamic branding spec §8). This card
// deliberately reverses D-048: the Branding scope was reopened by the
// 2026-06-11 design spec. footer_mode hidden/replace ride the sys-gated
// white_label_enabled entitlement; support contact is open to every org.

const MODES = [
  ['default', 'Powered by EducationXR', 'EducationXR glyph + text (the default).', false],
  ['hidden', 'Hide the footer', 'No branding line at all.', true],
  ['replace', 'Replace with our logo', '"Delivered by {your org}" with your white-label logo.', true]
]

export const BrandingDisplayCard = () => {
  const [settings, updateSettings] = useState(null)
  const [mode, updateMode] = useState('default')
  const [supportEmail, updateSupportEmail] = useState('')
  const [supportURL, updateSupportURL] = useState('')
  const [entitled, updateEntitled] = useState(false)
  const [busy, updateBusy] = useState(false)
  const [saved, updateSaved] = useState(false)
  const [error, updateError] = useState(null)

  useEffect(() => {
    lmsAdmin
      .getDisplaySettings()
      .then(result => {
        updateSettings(result)
        updateMode(result.footer_mode || 'default')
        updateSupportEmail(result.support_email || '')
        updateSupportURL(result.support_url || '')
        updateEntitled(!!result.white_label_enabled)
      })
      .catch(updateError)
  }, [])

  const patch = async payload => {
    if (busy) return
    updateBusy(true)
    updateSaved(false)
    updateError(null)
    try {
      const result = await lmsAdmin.patchDisplaySettings(payload)
      updateSettings(result)
      updateMode(result.footer_mode)
      updateSupportEmail(result.support_email || '')
      updateSupportURL(result.support_url || '')
      updateEntitled(!!result.white_label_enabled)
      updateSaved(true)
    } catch (e) {
      updateError(e)
    }
    updateBusy(false)
  }

  const save = () =>
    patch({
      footer_mode: mode,
      support_email: supportEmail.trim(),
      support_url: supportURL.trim()
    })

  if (!settings) return null

  return (
    <div className="lms-card" style={{ marginBottom: 14 }} data-testid="card-branding">
      <div className="lms-card-head">
        <div className="icon">
          <LmsIcon name="palette" />
        </div>
        <div className="body">
          <h2>Branding &amp; Display</h2>
        </div>
      </div>
      <div style={{ padding: '14px 18px', fontSize: 13 }}>
        <div className="field-help" style={{ marginBottom: 10 }}>
          Changes apply the next time a launcher loads — packages never need rebuilding.
        </div>

        {MODES.map(([value, label, help, gated]) => (
          <label key={value} className="branding-mode" style={{ display: 'flex', gap: 8, alignItems: 'flex-start', marginBottom: 8 }}>
            <input
              type="radio"
              name="footer-mode"
              value={value}
              checked={mode === value}
              disabled={gated && !entitled}
              onChange={() => updateMode(value)}
            />
            <span>
              <strong>{label}</strong>
              <span className="field-help" style={{ display: 'block' }}>{help}</span>
            </span>
          </label>
        ))}

        {/* Static preview strip (spec §8) — what the launcher footer will
            render for the selected mode. */}
        <div className="branding-preview" data-testid="branding-preview">
          {mode === 'default' && (
            <React.Fragment>
              <img src="/EDXR-Glyph.svg" alt="" />
              <strong>Powered by EducationXR</strong>
            </React.Fragment>
          )}
          {mode === 'replace' && <strong>Delivered by your organization (your white-label logo)</strong>}
          {mode === 'hidden' && <span className="field-help">No footer — the launcher card just ends.</span>}
        </div>

        {!entitled && (
          <div className="field-help" style={{ marginBottom: 10 }}>
            Contact EducationXR to enable white-labeling for hidden/replaced footers.
          </div>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 6 }}>
          <label>
            Support email
            <input
              className="form-control form-control-sm"
              type="email"
              value={supportEmail}
              onChange={e => updateSupportEmail(e.target.value)}
              placeholder="help@yourcompany.com"
            />
          </label>
          <label>
            Support link
            <input
              className="form-control form-control-sm"
              type="url"
              value={supportURL}
              onChange={e => updateSupportURL(e.target.value)}
              placeholder="https://help.yourcompany.com"
            />
          </label>
        </div>
        <div className="field-help" style={{ marginTop: 4 }}>
          Shown in launcher error screens instead of the generic "contact your training admin".
        </div>

        <LoginSysAdmin>
          <label data-testid="entitlement-toggle" style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 12 }}>
            <input
              type="checkbox"
              checked={entitled}
              onChange={e => patch({ white_label_enabled: e.target.checked })}
            />
            <span>
              <strong>White-label entitlement</strong>
              <span className="field-help" style={{ display: 'block' }}>System admins only — unlocks hidden/replaced footers for this organization.</span>
            </span>
          </label>
        </LoginSysAdmin>

        {error && (
          <div className="alert alert-danger" role="alert" style={{ marginTop: 10 }}>
            {error.message}
          </div>
        )}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 12 }}>
          <button className="btn btn-primary btn-sm" type="button" data-testid="branding-save" disabled={busy} onClick={() => save()}>
            Save branding
          </button>
          {saved && <span className="field-help">Saved.</span>}
        </div>
      </div>
    </div>
  )
}
